import { until } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { createRouter, createWebHistory, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

import {
  tokenService,
  OneTimeCodeForm,
  PasswordReset,
  PasswordResetConfirm,
  TwoFactorCodeForm,
  useAuthStore,
} from '@tallo/auth';
import { useInvestorStore } from '@tallo/investor/investor';
import { AppRouteNames, AppRoutes } from '@tallo/investor/navigation';
import { ShowingFilters } from '@tallo/investor/showing';
import { UserRole } from '@tallo/user';
import { TimeFilter } from '@tallo/utility';

import NotFound from '../components/404/not-found.vue';
import TransUnionReport from '../components/application/trans-union-report.vue';
import Auth from '../components/auth/auth.vue';
import ConfirmEmailChange from '../components/auth/confirm-email-change.vue';
import EmailConfirmationForm from '../components/auth/email-confirmation-form.vue';
import MissingPasswordSignIn from '../components/auth/missing-password-sign-in.vue';
import SignIn from '../components/auth/sign-in.vue';
import SignUp from '../components/auth/sign-up.vue';
import TeamInviteAccept from '../components/auth/team-invite-accept.vue';
import { buildingRoutes } from '../components/building/building.routes';
import Dashboard from '../components/dashboard/dashboard.vue';
import IntegrationDetailsPage from '../components/integrations/integration-details-page.vue';
import IntegrationsPage from '../components/integrations/integrations-page.vue';
import InvestorCalendar from '../components/investor-calendar.vue';
import InvestorShowings from '../components/investor-showings.vue';
import NewBuilding from '../components/new-listing/new-building.vue';
import { investorOnboardingRoutes } from '../components/onboarding/onboarding.routes';
import InvestorPortfolio from '../components/portfolio/investor-portfolio.vue';
import UserProfile from '../components/profile/user-profile.vue';
import { propertyOnboardingRoutes } from '../components/property-onboarding/property-onboarding.routes';
import {
  applicationsCountedFilters,
  applicationsCountPredicates,
  ApplicationsFilter,
  applicationsFilterOrders,
  applicationsFilterPredicates,
} from '../components/property/consts/applications-filter.const';
import { leadsFilterPredicates, PropertyLeadsFilter } from '../components/property/consts/leads-filter.const';
import PropertyAvailabilityPage from '../components/property/pages/availability/property-availability-page.vue';
import PropertyStatus from '../components/property/pages/dashboard/property-dashboard.vue';
import PropertyDetails from '../components/property/pages/details/property-details.vue';
import PropertyLeads from '../components/property/pages/leads/leads.vue';
import ShowingDetails from '../components/property/pages/showings/showing-details.vue';
import PropertyTeamPage from '../components/property/pages/team/property-team-page.vue';
import PropertyNavigation from '../components/property/property-navigation.vue';
import Property from '../components/property/property.vue';
import RenterProfileApplicationReview from '../components/renter-profile/renter-application-review.vue';
import RenterProfile from '../components/renter-profile/renter-profile.vue';
import CalendarCallback from '../components/settings/calendar-callback.vue';
import { authorizedGuard } from './authorized.guard';
import { hideOnProdGuard } from './hide-on-prod.guard';
import { oldRoutes } from './routes-old';

const routes: RouteRecordRaw[] = [
  ...oldRoutes,

  {
    path: AppRoutes.HOME,
    name: AppRouteNames.HOME,
    component: Dashboard,
    meta: { public: false },
    async beforeEnter() {
      const userStore = useInvestorStore();
      const { investor } = storeToRefs(userStore);
      const authStore = useAuthStore();
      // The load can also end by 401ing, which signs the seat out; waiting on the data alone never returns
      const isInvestorDataSettled = computed(() => Boolean(investor.value) || !authStore.isAuthenticated);
      const isInitialOnboardingCompleted = computed(() => investor.value?.onboarding?.initialOnboardingCompleted);
      const isPropertyOnboardingCompleted = computed(() => investor.value?.onboarding?.propertyOnboardingCompleted);

      await until(isInvestorDataSettled).toBeTruthy();

      if (!investor.value) {
        return { name: AppRouteNames.SIGN_IN };
      }

      if (!isInitialOnboardingCompleted.value) {
        return { name: AppRouteNames.INVESTOR_ONBOARDING };
      }

      if (!isPropertyOnboardingCompleted.value) {
        return { name: AppRouteNames.INVESTOR_PORTFOLIO };
      }

      return true;
    },
  },

  {
    name: AppRouteNames.INVESTOR_PORTFOLIO,
    path: AppRoutes.INVESTOR_PORTFOLIO,
    component: InvestorPortfolio,
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_PORTFOLIO_MANAGE,
    path: AppRoutes.INVESTOR_PORTFOLIO_MANAGE,
    component: () => import('../components/portfolio/manage/manage-portfolios-page.vue'),
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_CALENDAR,
    path: AppRoutes.INVESTOR_CALENDAR,
    component: InvestorCalendar,
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_PROFILE,
    path: AppRoutes.INVESTOR_PROFILE,
    component: UserProfile,
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_INTEGRATIONS,
    path: AppRoutes.INVESTOR_INTEGRATIONS,
    component: IntegrationsPage,
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_INTEGRATION_DETAILS,
    path: AppRoutes.INVESTOR_INTEGRATION_DETAILS,
    component: IntegrationDetailsPage,
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_SHOWINGS,
    path: AppRoutes.INVESTOR_SHOWINGS,
    component: InvestorShowings,
    meta: { public: false },
    beforeEnter: (to) => {
      if (!to.query.filter) {
        return { ...to, query: { ...to.query, filter: ShowingFilters.ALL } };
      }

      if (!to.query.timeFilter) {
        return { ...to, query: { ...to.query, timeFilter: TimeFilter.LAST_30_DAYS } };
      }
    },
  },
  {
    path: AppRoutes.INVESTOR_PROPERTY,
    name: AppRouteNames.INVESTOR_PROPERTY,
    components: {
      default: Property,
      subheader: PropertyNavigation,
    },
    meta: { public: false },
    children: [
      {
        path: AppRoutes.INVESTOR_PROPERTY_DETAILS,
        name: AppRouteNames.INVESTOR_PROPERTY_DETAILS,
        component: PropertyDetails,
      },
      {
        path: AppRoutes.INVESTOR_PROPERTY_DASHBOARD,
        name: AppRouteNames.INVESTOR_PROPERTY_DASHBOARD,
        component: PropertyStatus,
        beforeEnter: (to) => {
          if (!to.query.filter) {
            return { ...to, query: { ...to.query, filter: ShowingFilters.ALL } };
          }

          if (!to.query.timeFilter) {
            return { ...to, query: { ...to.query, timeFilter: TimeFilter.LAST_30_DAYS } };
          }
        },
      },
      {
        path: AppRoutes.INVESTOR_PROPERTY_SHOWINGS,
        name: AppRouteNames.INVESTOR_PROPERTY_SHOWINGS,
        component: InvestorShowings,
        beforeEnter: (to) => {
          if (!to.query.filter) {
            return { ...to, query: { ...to.query, filter: ShowingFilters.ALL } };
          }

          if (!to.query.timeFilter) {
            return { ...to, query: { ...to.query, timeFilter: TimeFilter.LAST_30_DAYS } };
          }
        },
      },
      {
        path: AppRoutes.INVESTOR_PROPERTY_LEADS,
        name: AppRouteNames.INVESTOR_PROPERTY_LEADS,
        component: PropertyLeads,
        props: {
          title: 'Leads',
          emptyResultTitle: "You don't have any leads yet",
          emptyResultDescription: 'Renters who inquire about properties and their conversations live here',
          filters: Object.values(PropertyLeadsFilter),
          filtersMap: leadsFilterPredicates,
        },
        beforeEnter: (to) => {
          if (!to.query.filter) {
            return { ...to, query: { ...to.query, filter: PropertyLeadsFilter.ALL } };
          }

          if (!to.query.timeFilter) {
            return { ...to, query: { ...to.query, timeFilter: TimeFilter.LAST_30_DAYS } };
          }
        },
      },
      {
        path: AppRoutes.INVESTOR_PROPERTY_APPLICATIONS,
        name: AppRouteNames.INVESTOR_PROPERTY_APPLICATIONS,
        component: PropertyLeads,
        props: {
          title: 'Applications',
          emptyResultTitle: 'No applications sent yet',
          emptyResultDescription: 'Renters who made it to the application stage live here',
          filters: Object.values(ApplicationsFilter),
          filtersMap: applicationsFilterPredicates,
          filtersOrder: applicationsFilterOrders,
          countedFilters: applicationsCountedFilters,
          countsMap: applicationsCountPredicates,
        },
        beforeEnter: (to) => {
          if (!to.query.filter) {
            return { ...to, query: { ...to.query, filter: ApplicationsFilter.ALL } };
          }

          if (!to.query.timeFilter) {
            return { ...to, query: { ...to.query, timeFilter: TimeFilter.LAST_30_DAYS } };
          }
        },
      },
      {
        path: AppRoutes.INVESTOR_PROPERTY_AVAILABILITY,
        name: AppRouteNames.INVESTOR_PROPERTY_AVAILABILITY,
        component: PropertyAvailabilityPage,
      },
      {
        path: AppRoutes.INVESTOR_PROPERTY_TEAM,
        name: AppRouteNames.INVESTOR_PROPERTY_TEAM,
        component: PropertyTeamPage,
      },
    ],
  },
  {
    path: AppRoutes.INVESTOR_PROPERTY_RENTER_PROFILE,
    name: AppRouteNames.INVESTOR_PROPERTY_RENTER_PROFILE,
    component: RenterProfile,
    meta: { public: false },
  },
  {
    path: AppRoutes.INVESTOR_PROPERTY_RENTER_PROFILE_APPLICATION_REVIEW,
    name: AppRouteNames.INVESTOR_PROPERTY_RENTER_PROFILE_APPLICATION_REVIEW,
    component: RenterProfileApplicationReview,
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_PROPERTY_SHOWING_DETAILS,
    path: AppRoutes.INVESTOR_PROPERTY_SHOWING_DETAILS,
    component: ShowingDetails,
    meta: { public: false },
  },

  {
    name: AppRouteNames.INVESTOR_NEW_BUILDING,
    path: AppRoutes.INVESTOR_NEW_BUILDING,
    component: NewBuilding,
    meta: { public: false },
  },
  {
    ...buildingRoutes,
  },

  {
    ...propertyOnboardingRoutes,
  },

  {
    ...investorOnboardingRoutes,
  },

  {
    path: AppRoutes.AUTH,
    component: Auth,
    meta: { public: true },
    // Guarded per page rather than here: the invite link must open for a signed-in visitor so the page
    // can offer to sign out, and it stays under /auth so the signed-in chrome never draws over it
    children: [
      {
        name: AppRouteNames.SIGN_UP,
        path: AppRoutes.SIGN_UP,
        component: SignUp,
        beforeEnter: [authorizedGuard],
      },
      {
        name: AppRouteNames.SIGN_UP_CONFIRM_EMAIL,
        path: AppRoutes.SIGN_UP_CONFIRM_EMAIL,
        component: EmailConfirmationForm,
        beforeEnter: [authorizedGuard],
      },
      {
        name: AppRouteNames.TEAM_INVITE,
        path: AppRoutes.TEAM_INVITE,
        component: TeamInviteAccept,
      },
      {
        name: AppRouteNames.SIGN_IN,
        path: AppRoutes.SIGN_IN,
        component: SignIn,
        beforeEnter: [authorizedGuard],
      },
      {
        name: AppRouteNames.RESET_PASSWORD,
        path: AppRoutes.RESET_PASSWORD,
        component: PasswordReset,
        beforeEnter: [authorizedGuard],
        props: {
          backToSignInRoute: {
            name: AppRouteNames.SIGN_IN,
          },
          forRole: UserRole.INVESTOR,
        },
      },
      {
        name: AppRouteNames.RESET_PASSWORD_CONFIRM,
        path: AppRoutes.RESET_PASSWORD_CONFIRM,
        component: PasswordResetConfirm,
        beforeEnter: [authorizedGuard],
        props: {
          backToSignInRoute: {
            name: AppRouteNames.SIGN_IN,
          },
          afterLoginRoute: {
            name: AppRouteNames.INVESTOR_PORTFOLIO,
          },
          forRole: UserRole.INVESTOR,
        },
      },
      {
        name: AppRouteNames.MISSING_PASSWORD_SIGN_IN,
        path: AppRoutes.MISSING_PASSWORD_SIGN_IN,
        component: MissingPasswordSignIn,
        beforeEnter: [authorizedGuard],
      },
      {
        name: AppRouteNames.ONE_TIME_CODE_SIGN_IN,
        path: AppRoutes.ONE_TIME_CODE_SIGN_IN,
        component: OneTimeCodeForm,
        beforeEnter: [authorizedGuard],
        props: {
          navigationAfterVerification: {
            name: AppRouteNames.INVESTOR_PORTFOLIO,
          },
        },
      },
      {
        name: AppRouteNames.TWO_FACTOR_SIGN_IN,
        path: AppRoutes.TWO_FACTOR_SIGN_IN,
        component: TwoFactorCodeForm,
        beforeEnter: [authorizedGuard],
        props: {
          afterLoginRoute: {
            name: AppRouteNames.INVESTOR_PORTFOLIO,
          },
          backToSignInRoute: {
            name: AppRouteNames.SIGN_IN,
          },
        },
      },
    ],
  },

  // Outside the auth block: the email-change link must work for signed-in users too,
  // and the auth block's authorizedGuard would bounce them to the portfolio
  {
    name: AppRouteNames.CONFIRM_EMAIL_CHANGE,
    path: AppRoutes.CONFIRM_EMAIL_CHANGE,
    component: ConfirmEmailChange,
    meta: { public: true },
  },

  {
    name: AppRouteNames.APPLICATION_REPORT,
    path: AppRoutes.APPLICATION_REPORT,
    meta: { public: false },
    component: TransUnionReport,
  },

  {
    name: AppRouteNames.DEV_TOOLS,
    path: AppRoutes.DEV_TOOLS,
    meta: { public: false },
    beforeEnter: [hideOnProdGuard],
    component: () => import('../components/dev-tools/dev-tools.vue'),
  },

  {
    path: '/settings/calendar',
    component: CalendarCallback,
    meta: { public: false },
  },

  {
    name: AppRouteNames.NOT_FOUND,
    path: AppRoutes.NOT_FOUND,
    component: NotFound,
    meta: { public: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// The single enforcement point for the 2FA gate: a sign-in that hit it can't reach anything but the
// code screen until it's cleared, and the intended destination is remembered so it resumes after.
// Runs BEFORE the token check below — a 2FA-pending user has no token yet, so that check would
// otherwise bounce them to sign-in before this could route them to the code screen.
router.beforeEach((to: RouteLocationNormalized) => {
  const authStore = useAuthStore();
  if (!authStore.isTwoFactorPending || to.name === AppRouteNames.TWO_FACTOR_SIGN_IN) {
    return true;
  }

  if (to.name === AppRouteNames.SIGN_IN) {
    authStore.cancelTwoFactor();
    return true;
  }

  authStore.rememberPostSignInRedirect(to.fullPath);
  return { name: AppRouteNames.TWO_FACTOR_SIGN_IN };
});

// The destination survives the bounce so a deep link (a text from the assistant, an email) lands
// where it pointed once the user is in, whichever sign-in method they use.
router.beforeEach((to: RouteLocationNormalized) => {
  if (!to.meta.public && !tokenService.getToken()) {
    const authStore = useAuthStore();
    authStore.logout();
    authStore.rememberPostSignInRedirect(to.fullPath);

    return { name: AppRouteNames.SIGN_IN };
  }
});

export default router;
